import curiumLogo from '@client/assets/curium-logo.svg';
import { PageLoading } from '@client/components/_ui/feedback/PageLoading';
import type { ReactNode } from 'react';

interface AuthLoadingScreenProps {
    /** Short heading, e.g. "Signing you in" */
    title: ReactNode;
    /** Single muted status line shown under the progress bar */
    hint: ReactNode;
}

/**
 * Frameless full-page loading state for auth flows (login callback, org switch,
 * admin consent): wordmark, heading, indeterminate accent progress bar, hint.
 *
 * The static boot splash in client/index.html is a pre-React twin of this layout —
 * keep its geometry, colours, and sweep animation in sync when changing this.
 *
 * The wordmark is the LCP element on /login and /callback. It is imported through Vite
 * (inlined as a data URI under `assetsInlineLimit`) rather than fetched from /static/,
 * which the edge serves from R2 per request (~400 ms from a cold colo). index.html uses
 * the same asset, so the splash and this twin share one already-decoded image.
 */
export function AuthLoadingScreen({ title, hint }: AuthLoadingScreenProps) {
    return (
        <PageLoading>
            <div role="status" className="flex flex-col items-center px-6 py-16 text-center md:py-0">
                <img src={curiumLogo} alt="Curium" className="h-9 w-auto dark:invert" />
                <h2 className="mt-9 font-semibold text-foreground text-xl">{title}</h2>
                <div className="auth-progress mt-6" aria-hidden="true" />
                <p className="mt-5 text-sm text-subtle-foreground">{hint}</p>
            </div>
        </PageLoading>
    );
}
